import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FiVideo, FiMic, FiPhone, FiUsers, FiUserCheck } from 'react-icons/fi';
import io from 'socket.io-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { API_URL, JAAS_APP_ID } from '../config/api';
import { shouldSendMeetingEntered, sendMeetingEntered } from '../lib/meetingEntered';

// Lazy load JaaS SDK to improve initial page load; retry once after a short
// delay so a transient network drop doesn't permanently break the import
const loadJitsiSdk = () =>
  import('@jitsi/react-sdk').catch(
    () => new Promise((resolve) => setTimeout(resolve, 2000)).then(() => import('@jitsi/react-sdk'))
  );
const JaaSMeeting = React.lazy(() =>
  loadJitsiSdk().then(module => ({ default: module.JaaSMeeting }))
);

// What anyone holding a link the server no longer accepts is told. The page
// cannot tell a clinician's link from a patient's once the link is unreadable,
// so it speaks to both. It used to say nothing: a clinician was shown the
// patients' waiting room, and a patient waited for an admission that could
// never come (13-10-2026, server docs/KEYRING.md).
const LINK_NOT_ACCEPTED =
  'This meeting link is no longer valid. If you are the clinician, open the visit in the clinic app to use its current link. If you are the patient, please contact the clinic for a new link.';

// Stable per-tab patient identity so refreshes and re-requests upsert the
// same waiting-room entry instead of creating duplicates
const getStablePatientId = (roomId) => {
  const key = `blh-meet-patient-${roomId}`;
  try {
    let id = sessionStorage.getItem(key);
    if (!id) {
      id = `patient-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem(key, id);
    }
    return id;
  } catch (e) {
    return `patient-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
};

const Meeting = () => {
  const { roomId } = useParams();
  const [searchParams] = useSearchParams();

  // The link token. The server now requires it on every endpoint that writes to
  // a consultation — it is what proves this browser was invited to THIS visit,
  // in this tenant, rather than merely knowing a room id. Read once so every
  // call sends the same thing.
  const meetingToken = searchParams.get('t');

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [waitingRoom, setWaitingRoom] = useState([]);
  const [isAdmitted, setIsAdmitted] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [patientId, setPatientId] = useState(null);
  const [wasRejected, setWasRejected] = useState(false);
  const [jitsiLoadFailed, setJitsiLoadFailed] = useState(false);
  const [inMeeting, setInMeeting] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [transcriptionChunks, setTranscriptionChunks] = useState([]);
  const [transcriptionPaused, setTranscriptionPaused] = useState(false);
  const [meetingStartTime, setMeetingStartTime] = useState(null);
  const [leftInternal, setLeftInternal] = useState(false);
  const [callEnded, setCallEnded] = useState(false);
  const meetingStartTimeRef = useRef(null);
  // The JaaS session id — 8x8's own identity for this meeting, and the SAME
  // value that arrives in their server-to-server webhooks. Both delivery paths
  // key the stored transcript on it, which is what lets the server merge them
  // into one document instead of recording the visit twice. It is deliberately
  // NOT a page-load id: a mid-visit refresh must not start a second transcript.
  const jaasSessionIdRef = useRef(null);
  const transcriptionPausedRef = useRef(false);
  // Whether the transcriber has ever actually joined this meeting. Auto-start
  // asks for it; only this says it arrived.
  const transcriberEverOnRef = useRef(false);
  const socketRef = useRef(null);
  const apiRef = useRef(null);
  const isInBreakoutRef = useRef(false);
  const finalizeTimeoutRef = useRef(null);
  const initialSessionCreatedRef = useRef(false);
  const meetingEnteredSentRef = useRef(false);
  const attendeesRef = useRef(new Map());
  const prevWaitingIdsRef = useRef(new Set());

  // A staff call or meeting minted from Messaging. No appointment, no patient,
  // no waiting room and never a transcript: every clinical branch below is off.
  const isInternal = config?.meetingKind === 'internal';

  // Simple toast notification
  const showToast = (title, description, type = 'info') => {
    setToastMessage({ title, description, type });
    setTimeout(() => setToastMessage(null), 5000);
  };

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}:${String(minutes % 60).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
    } else if (minutes > 0) {
      return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
    }
    return `0:${String(seconds).padStart(2, '0')}`;
  };

  useEffect(() => {
    validateMeeting();
    return () => {
      if (finalizeTimeoutRef.current) {
        clearTimeout(finalizeTimeoutRef.current);
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      if (window.jitsiAPI) {
        window.jitsiAPI.dispose();
        window.jitsiAPI = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // Safety net: finalize transcription if tab is closed unexpectedly
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (jaasSessionIdRef.current && config?.appointmentId && config?.userRole === 'moderator') {
        const blob = new Blob([JSON.stringify({
          // The server reads the appointment and tenant from this token, and
          // requires a moderator one: this beacon asserts "the clinician has
          // left", which is what ends a consultation and releases the note.
          t: meetingToken,
          roomId,
          jaasSessionId: jaasSessionIdRef.current
        // text/plain keeps this a CORS-simple request. The API is on another
        // host, and application/json would force a preflight — issued while the
        // page is being torn down, which is when it is least likely to finish.
        })], { type: 'text/plain' });
        navigator.sendBeacon(`${API_URL}/api/public/meeting/finalize-transcription`, blob);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [config, roomId]);

  // Doctor: initial fetch + periodic reconciliation of the waiting room
  // (covers patient-waiting events lost while the socket was down)
  useEffect(() => {
    if (config?.userRole !== 'moderator' || config?.meetingKind === 'internal') return;
    fetchWaitingRoom();
    const interval = setInterval(fetchWaitingRoom, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // Patient: poll admission status as a fallback for lost socket events,
  // and re-register if a server restart dropped the waiting entry
  useEffect(() => {
    if (!config || config.userRole === 'moderator') return;
    if (!isWaiting || isAdmitted || wasRejected || !patientId) return;

    const checkAdmission = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/meeting/check-admission/${roomId}/${patientId}?t=${encodeURIComponent(meetingToken || '')}`);
        if (response.status === 401 || response.status === 403) {
          // The link stopped being accepted while waiting: say so, rather than
          // keep a patient in a lobby no clinician can see.
          setIsWaiting(false);
          setError(LINK_NOT_ACCEPTED);
          return;
        }
        if (!response.ok) return;
        const data = await response.json();
        if (data.admitted) {
          setIsAdmitted(true);
          setIsWaiting(false);
          showToast('Admitted!', 'You have been admitted to the meeting', 'success');
        } else if (data.rejected) {
          setIsWaiting(false);
          setWasRejected(true);
          showToast('Access Denied', 'Your request to join was denied', 'error');
        } else if (!(data.waitingRoom || []).some(p => p.id === patientId)) {
          // Entry vanished (server restart / cleanup race): re-register silently
          await fetch(`${API_URL}/api/public/meeting/request-join`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              t: meetingToken,
              roomId,
              patientId,
              patientInfo: {
                name: config.userName || 'Patient',
                email: config.userEmail || ''
              }
            })
          });
        }
      } catch (error) {
        console.error('Error checking admission status:', error);
      }
    };

    const interval = setInterval(checkAdmission, 5000);
    // React instantly when connectivity returns instead of waiting a tick
    window.addEventListener('online', checkAdmission);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', checkAdmission);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, isWaiting, isAdmitted, wasRejected, patientId, roomId]);

  const validateMeeting = async () => {
    try {
      // Get token from URL parameter
      const token = searchParams.get('t');

      const response = await fetch(`${API_URL}/api/public/meeting/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId, token })
      });

      if (!response.ok) {
        let body = null;
        try {
          body = await response.json();
        } catch (e) {
          // Non-JSON error body: fall through to the generic error below.
        }
        if (response.status === 410 && body?.code === 'CALL_ENDED') {
          setCallEnded(true);
          setLoading(false);
          return;
        }
        // 401/403: the server does not accept this link. Anything else is the
        // server failing, which a reload may cure.
        throw new Error(response.status === 401 || response.status === 403
          ? LINK_NOT_ACCEPTED
          : 'The video consultation could not be reached. Please reload the page in a moment.');
      }

      const data = await response.json();
      setConfig(data);
      setLoading(false);

      // Internal rooms have no waiting room: no /meeting socket, no admission state.
      if (data.meetingKind === 'internal') return;

      // Initialize socket connection
      initializeSocket(data);

      // Restore waiting/admitted state after a reload using the stable id,
      // so recovering from a failed Jitsi load doesn't restart the flow
      if (data.userRole !== 'moderator') {
        try {
          const stableId = getStablePatientId(roomId);
          const statusResponse = await fetch(`${API_URL}/api/public/meeting/check-admission/${roomId}/${stableId}?t=${encodeURIComponent(meetingToken || '')}`);
          if (statusResponse.ok) {
            const status = await statusResponse.json();
            if (status.admitted) {
              setPatientId(stableId);
              setIsAdmitted(true);
            } else if ((status.waitingRoom || []).some(p => p.id === stableId)) {
              setPatientId(stableId);
              setIsWaiting(true);
            }
          }
        } catch (e) {
          console.error('Error restoring waiting state:', e);
        }
      }
    } catch (err) {
      setError(err.message || 'Unable to join meeting');
      setLoading(false);
    }
  };

  const initializeSocket = (configData) => {
    // Don't initialize twice
    if (socketRef.current && socketRef.current.connected) {
      return;
    }

    const currentUserId = configData.userId || configData.userName;
    const stablePatientId = configData.userRole === 'moderator' ? null : getStablePatientId(roomId);

    // Connect following project pattern - using namespace and auth.
    //
    // `t` is the link's token, the same one every HTTP call carries. The
    // server reads the room and the role from it and decides which rooms this
    // socket joins; `rooms`, `roomId` and `userRole` below are what older
    // servers read, and go once the server no longer looks at them.
    socketRef.current = io(API_URL + "/meeting", {
      // The load balancer keeps a socket.io session on one server task through its
      // stickiness cookie (AWSALBCORS); without credentials the browser never sends
      // it, the next poll lands on another task, and the waiting room reconnects in a
      // loop (400 "Session ID unknown").
      withCredentials: true,
      auth: {
        t: meetingToken,
        rooms: configData.userRole === 'moderator'
          ? [`room-${roomId}-doctor`]
          : [`room-${roomId}-waiting`],
        userId: currentUserId,
        roomId: roomId,
        userRole: configData.userRole,
        ...(stablePatientId ? { patientId: stablePatientId } : {})
      }
    });

    socketRef.current.on('connect', () => {
      console.log('Socket connected to meeting namespace');
      // Reconnects miss events emitted while offline; re-sync the list
      if (configData.userRole === 'moderator') {
        fetchWaitingRoom();
      }
    });

    if (configData.userRole === 'moderator') {
      // Doctor: listen for waiting patients

      // Listen for patients in waiting room - This should work for broadcast
      socketRef.current.on('patient-waiting', (data) => {
        console.log('🔔 Patient waiting event received:', data);
        // Check if this event is for our room
        if (data.roomId === roomId) {
          console.log('Patient is for this room:', data.patient);

          // Update waiting room immediately (upsert by id)
          setWaitingRoom(prev => {
            const exists = prev.some(p => p.id === data.patient.id);
            if (!exists) return [...prev, data.patient];
            return prev.map(p => (p.id === data.patient.id ? data.patient : p));
          });

          // Toast only for patients not already in the list (re-requests
          // from the same patient re-emit this event)
          if (!prevWaitingIdsRef.current.has(data.patient.id)) {
            prevWaitingIdsRef.current.add(data.patient.id);
            showToast(
              'Patient Waiting',
              `${data.patient.name} is waiting to join the meeting`,
              'info'
            );
          }
        } else {
          console.log('Event for different room. Expected:', roomId, 'Got:', data.roomId);
        }
      });

      // Server-pushed full refresh (upserts, admits/rejects, disconnect cleanup)
      socketRef.current.on('waiting-room-updated', (data) => {
        if (data.roomId === roomId) {
          applyWaitingRoom(data.waitingRoom);
        }
      });
    } else {
      // Patient: store socket ref to listen for admission later
      // The actual listener will be set up after requestJoinMeeting when we have patientId
    }
  };

  // Replace the doctor's list with the server's; toast only for new arrivals
  // so periodic reconciliation doesn't spam repeated notifications
  const applyWaitingRoom = (list) => {
    const next = Array.isArray(list) ? list : [];
    const prevIds = prevWaitingIdsRef.current;
    const newPatients = next.filter(p => !prevIds.has(p.id));
    prevWaitingIdsRef.current = new Set(next.map(p => p.id));
    setWaitingRoom(next);
    if (newPatients.length > 0) {
      showToast(
        'Patient Waiting',
        `${newPatients.map(p => p.name).join(', ')} is waiting to join the meeting`,
        'info'
      );
    }
  };

  const fetchWaitingRoom = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/meeting/waiting-room/${roomId}?t=${encodeURIComponent(meetingToken || '')}`);
      if (response.ok) {
        const data = await response.json();
        applyWaitingRoom(data.waitingRoom);
      }
    } catch (error) {
      console.error('Error fetching waiting room:', error);
    }
  };

  const requestJoinMeeting = async () => {
    try {
      setIsWaiting(true);
      setWasRejected(false);
      const stableId = getStablePatientId(roomId);
      const response = await fetch(`${API_URL}/api/public/meeting/request-join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          t: meetingToken,
          roomId,
          patientId: stableId,
          patientInfo: {
            name: config.userName || 'Patient',
            email: config.userEmail || ''
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        const newPatientId = data.patientId;
        setPatientId(newPatientId);
        console.log('Patient ID set to:', newPatientId);

        // NOW listen for admission status for THIS specific patient
        if (socketRef.current) {
          // Remove any existing listeners first
          socketRef.current.off('admission-status');

          socketRef.current.on('admission-status', (admissionData) => {
            if (admissionData.patientId === newPatientId && admissionData.roomId === roomId) {
              if (admissionData.admitted) {
                setIsAdmitted(true);
                setIsWaiting(false);
                showToast('Admitted!', 'You have been admitted to the meeting', 'success');
              } else {
                setIsWaiting(false);
                setWasRejected(true);
                showToast('Access Denied', 'Your request to join was denied', 'error');
              }
            }
          });
        }

        showToast('Request Sent', 'Waiting for the clinician to admit you', 'info');
      } else if (response.status === 401 || response.status === 403) {
        // The server did not register the request: no clinician will see it.
        // It used to leave the page "waiting" here, forever.
        setIsWaiting(false);
        setError(LINK_NOT_ACCEPTED);
      } else {
        setIsWaiting(false);
        showToast('Error', 'Your request could not be sent. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error requesting to join:', error);
      setIsWaiting(false);
      showToast('Error', 'Failed to request access to the meeting', 'error');
    }
  };

  const admitPatient = async (patient, admitted) => {
    try {
      const response = await fetch(`${API_URL}/api/public/meeting/admit-patient`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          t: meetingToken,
          roomId,
          patientId: patient.id,
          admitted
        })
      });

      if (response.ok) {
        // Remove from waiting room
        setWaitingRoom(prev => prev.filter(p => p.id !== patient.id));
        showToast(
          admitted ? 'Patient Admitted' : 'Patient Rejected',
          `${patient.name} has been ${admitted ? 'admitted to' : 'rejected from'} the meeting`,
          admitted ? 'success' : 'warning'
        );
      }
    } catch (error) {
      console.error('Error admitting patient:', error);
      showToast('Error', 'Failed to process admission', 'error');
    }
  };

  // PAUSE HAS TO STOP THE TRANSCRIBER, NOT JUST OUR RELAY.
  //
  // This used to set a local flag and nothing else. The flag suppressed the
  // browser's copy of each phrase — and that was all it did. 8x8 kept
  // transcribing, and its TRANSCRIPTION_CHUNK_RECEIVED webhook kept writing
  // every word into the patient's transcript server-side. The button said
  // "Paused", the banner told the clinician to use it, and the consultation was
  // recorded regardless.
  //
  // That is the wrong way round for the only reason anyone presses it: stepping
  // aside for a private word, or a patient asking that something not be taken
  // down. `setSubtitles(false)` stops the transcriber itself, which is what
  // stops the webhook — at the source, where the promise made to the patient
  // can actually be kept.
  //
  // Resuming starts capturing again from that moment; JaaS does not transcribe
  // retroactively, which is exactly the semantics a pause should have.
  const handleTogglePause = () => {
    const newPaused = !transcriptionPausedRef.current;
    transcriptionPausedRef.current = newPaused;
    setTranscriptionPaused(newPaused);
    setTranscription(apiRef.current, !newPaused);
  };

  // Set the transcription state EXPLICITLY rather than toggling it.
  //
  // `toggleSubtitles` flips whatever the current state happens to be, so any
  // path that fires twice — a retry, an overlapping timer, a fallback that runs
  // after the primary path already succeeded — silently turns transcription
  // back OFF. That is exactly how returning from a breakout room used to leave
  // the rest of a consultation untranscribed. `setSubtitles` is idempotent, so
  // calling it twice is harmless by construction.
  // POSITIONAL ARGUMENTS. Not an options object, however much it looks like one.
  //
  //   external_api.js:  executeCommand(name, ...args)   // args forwarded as a list
  //   API.js:           'set-subtitles': (enabled, displaySubtitles, language) => ...
  //
  // Passing `{enabled: false, ...}` put the whole OBJECT in the `enabled`
  // parameter, and an object is truthy. So every call — pause and resume alike —
  // asked to turn transcription ON. That is the button a clinician reported as
  // "it can start it but never stop it, whatever state it shows": accurate, and
  // it meant pause had still never once paused. The fix in 10.6 was correct
  // about where to intervene and wrong about how to call it.
  //
  // Third argument stays null: a language here means *translate into* it
  // (`translation-languages:en`), which is a different service. We want the
  // consultation transcribed as spoken.
  const setTranscription = (externalApi, enabled) => {
    if (!externalApi) return;
    try {
      externalApi.executeCommand('setSubtitles', enabled, enabled, null);
    } catch (error) {
      console.error('Failed to set transcription state:', error);
    }
  };

  // `getSessionId()` is documented as empty on the prejoin screen and "not
  // guaranteed to be available immediately after joining", so it is polled
  // rather than read once. Until it resolves, relayed chunks are skipped by the
  // server on purpose — the webhook carries the same phrases, and inventing an
  // id here would fragment the transcript, which is the failure this redesign
  // exists to eliminate.
  const resolveJaasSessionId = async (externalApi, { attempts = 10, delayMs = 500 } = {}) => {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      try {
        const sessionId = await externalApi.getSessionId();
        if (sessionId) {
          jaasSessionIdRef.current = sessionId;
          return sessionId;
        }
      } catch (error) {
        console.error('getSessionId failed:', error);
      }
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    console.error('Could not resolve the JaaS session id; relying on the webhook path');
    return null;
  };

  // The react-sdk caches the external_api.js script promise at module level,
  // including rejections, so a failed load cannot be retried by remounting.
  // A full reload is the only reliable recovery; waiting/admitted state is
  // restored on load from the stable patient id (see validateMeeting)
  const retryJitsiLoad = () => {
    window.location.reload();
  };

  // If the Jitsi API isn't ready shortly after mounting the meeting view
  // (script failed during a network drop), surface a retry overlay
  useEffect(() => {
    const inMeetingView = config && (config.meetingKind === 'internal' || config.userRole === 'moderator' || isAdmitted);
    if (!inMeetingView || jitsiLoadFailed) return;
    const timer = setTimeout(() => {
      if (!apiRef.current) setJitsiLoadFailed(true);
    }, 12000);
    return () => clearTimeout(timer);
  }, [config, isAdmitted, jitsiLoadFailed]);

  // Recover automatically as soon as connectivity returns: give the embed
  // a moment in case it loads by itself, then force the reload
  useEffect(() => {
    const handleOnline = () => {
      const inMeetingView = config && (config.meetingKind === 'internal' || config.userRole === 'moderator' || isAdmitted);
      if (!inMeetingView || apiRef.current) return;
      setTimeout(() => {
        if (!apiRef.current) retryJitsiLoad();
      }, 2000);
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [config, isAdmitted]);

  // Loading state
  if (loading) {
    return (
      <div className="h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Spinner className="h-12 w-12" />
          <p className="text-lg text-gray-600">
            Connecting to video consultation...
          </p>
        </div>
      </div>
    );
  }

  if (callEnded) {
    return (
      <div className="h-screen bg-white flex items-center justify-center">
        <div className="max-w-md text-center space-y-3">
          <FiPhone className="mx-auto h-12 w-12 text-gray-400" />
          <h1 className="text-xl font-semibold text-gray-800">This call has ended</h1>
          <p className="text-gray-600">Start a new call from Messaging. You can close this tab.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-screen bg-white flex items-center justify-center">
        <div className="max-w-md text-center space-y-6">
          <FiVideo className="mx-auto h-16 w-16 text-red-400" />
          <h1 className="text-2xl font-semibold text-gray-800">
            Unable to Join Meeting
          </h1>
          <p className="text-gray-600">
            {error}
          </p>
          <p className="text-sm text-gray-500">
            If the problem continues, please contact the clinic.
          </p>
          <Button
            onClick={() => window.location.href = 'https://brightlight.ai'}
            className="mt-4"
          >
            Return to Main Site
          </Button>
        </div>
      </div>
    );
  }

  if (leftInternal) {
    return (
      <div className="h-screen bg-white flex items-center justify-center">
        <div className="max-w-md text-center space-y-3">
          <FiPhone className="mx-auto h-12 w-12 text-gray-400" />
          <h1 className="text-xl font-semibold text-gray-800">You left the call</h1>
          <p className="text-gray-600">You can close this tab.</p>
        </div>
      </div>
    );
  }

  // Patient Waiting Room UI - Show this for patients who haven't been admitted yet
  // Only a link the server accepted as a participant's reaches it: an
  // unreadable or expired link is refused by /validate and shown as an error.
  if (config && !isInternal && config.userRole !== 'moderator' && !isAdmitted) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md">
          <Card className="shadow-lg">
            <CardHeader className="text-center">
              <FiVideo className="mx-auto h-16 w-16 text-blue-500 mb-4" />
              <CardTitle className="text-2xl">Virtual Consultation Waiting Room</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {!isWaiting && !patientId && !wasRejected && (
                <>
                  <CardDescription className="text-center">
                    Click below to request access to your video consultation.
                    The clinician will be notified and admit you when ready.
                  </CardDescription>
                  <Button
                    onClick={requestJoinMeeting}
                    size="lg"
                    className="w-full"
                  >
                    <FiUserCheck className="mr-2" />
                    Request to Join Meeting
                  </Button>
                </>
              )}

              {wasRejected && (
                <div className="text-center space-y-4">
                  <CardDescription className="text-center">
                    Your request to join was declined by the clinician.
                  </CardDescription>
                  <Button
                    onClick={requestJoinMeeting}
                    size="lg"
                    variant="outline"
                    className="w-full"
                  >
                    <FiUserCheck className="mr-2" />
                    Request Again
                  </Button>
                </div>
              )}

              {isWaiting && (
                <div className="text-center space-y-4">
                  <Spinner className="mx-auto h-12 w-12" />
                  <p className="text-lg text-gray-600">
                    Waiting for the clinician to admit you...
                  </p>
                  <p className="text-sm text-gray-500">
                    Please stay on this page. You'll be automatically connected once admitted.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Doctor Waiting Room Management UI
  const WaitingRoomPanel = () => {
    if (config?.userRole !== 'moderator' || isInternal || waitingRoom.length === 0) return null;

    return (
      <div className="fixed top-20 right-4 bg-white p-4 rounded-lg shadow-2xl max-w-sm z-[9999]">
        <div className="flex items-center mb-3">
          <FiUsers className="text-blue-500 mr-2" />
          <h3 className="text-sm font-semibold flex-1">Waiting Room</h3>
          <Badge className="ml-auto">{waitingRoom.length}</Badge>
        </div>
        <div className="space-y-2">
          {waitingRoom.map((patient) => (
            <div key={patient.id} className="p-3 bg-gray-50 rounded-md">
              <p className="font-medium">{patient.name}</p>
              <p className="text-xs text-gray-500">
                Waiting since {new Date(patient.requestTime).toLocaleTimeString()}
              </p>
              <div className="flex gap-2 mt-2">
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => admitPatient(patient, true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Admit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => admitPatient(patient, false)}
                  className="text-red-600 border-red-600 hover:bg-red-50"
                >
                  Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Video Meeting Room
  return (
    <div className="h-screen w-screen bg-black relative">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-4 right-4 z-[10000]">
          <Alert className={`
            ${toastMessage.type === 'success' ? 'border-green-500 bg-green-50' : ''}
            ${toastMessage.type === 'error' ? 'border-red-500 bg-red-50' : ''}
            ${toastMessage.type === 'warning' ? 'border-yellow-500 bg-yellow-50' : ''}
            ${toastMessage.type === 'info' ? 'border-blue-500 bg-blue-50' : ''}
          `}>
            <AlertDescription>
              <strong>{toastMessage.title}</strong>
              {toastMessage.description && <p className="text-sm mt-1">{toastMessage.description}</p>}
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Waiting Room Panel for Doctors - Always show for moderators */}
      {config?.userRole === 'moderator' && <WaitingRoomPanel />}

      {/* Transcription control - bottom-right corner.
       *
       * Shown for the whole meeting, not only while transcribing. It used to be
       * gated on `isTranscribing`, which made it a one-way control: the
       * clinician could stop capture and then watched the button vanish,
       * leaving the toolbar or the captions panel as the only way back on. The
       * gate went unnoticed because `isTranscribing` could only ever be set to
       * true — the listener that would have cleared it was subscribed to an
       * event name that does not exist — so in practice the button never
       * disappeared. Fixing that listener is what exposed this.
       *
       * A control that disappears in one of its two states is not a control.
       */}
      {config?.userRole === 'moderator' && !isInternal && inMeeting && (
        <div className="fixed bottom-6 right-6 z-[10000] flex flex-col items-center gap-1">
          <span className="text-white/60 text-[10px] font-medium tracking-wide">
            {transcriptionPaused ? 'Paused' : 'Live'}
          </span>
          <button
            onClick={handleTogglePause}
            title={transcriptionPaused ? 'Start transcription' : 'Pause transcription'}
            className={`
              w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-150 shadow-lg
              ${transcriptionPaused
                ? 'bg-yellow-500/90 hover:bg-yellow-400 text-white'
                : 'bg-white/15 hover:bg-white/25 text-white border border-white/20'}
            `}
          >
            {transcriptionPaused ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
              </svg>
            )}
          </button>
        </div>
      )}
      {jitsiLoadFailed && (
        <div className="fixed inset-0 z-[9998] bg-black flex items-center justify-center">
          <div className="text-center space-y-4 max-w-sm">
            <FiVideo className="mx-auto h-12 w-12 text-red-400" />
            <p className="text-white text-lg">
              We couldn't load the video call. Please check your connection.
            </p>
            <Button onClick={retryJitsiLoad} size="lg">
              Retry
            </Button>
          </div>
        </div>
      )}
      <React.Suspense
        fallback={
          <div className="h-screen bg-black flex items-center justify-center">
            <div className="text-center">
              <Spinner className="mx-auto text-white" />
              <p className="text-white mt-4">Loading video conference...</p>
            </div>
          </div>
        }
      >
        <JaaSMeeting
          appId={JAAS_APP_ID}
          roomName={config.roomId || roomId} // Use config.roomId or fallback to URL param
          jwt={config.jaasJWT}

          configOverwrite={{
            subject: isInternal ? 'Brightlight Health - Team call' : 'BrightLight Health - Virtual Consultation',
            prejoinPageEnabled: true,
            startWithAudioMuted: false,
            startWithVideoMuted: false,
            disableModeratorIndicator: false,
            enableWelcomePage: false,
            disableInviteFunctions: isInternal || config.userRole !== 'moderator',
            doNotStoreRoom: true,
            dialInNumbersUrl: 'https://8x8.vc/v1/_jaas/vmms-conference-mapper/access/v1/dids',
            dialInConfCodeUrl: 'https://8x8.vc/v1/_jaas/vmms-conference-mapper/v1/access',
            enableNoisyMicDetection: true,
            resolution: 720,
            remoteVideoMenu: {
              disablePrivateChat: true,
            },
            ...(config.userRole !== 'moderator' ? {
              breakoutRooms: {
                hideJoinRoomButton: true,
              },
            } : {}),
            // Audio quality: 64kbps Opus for clear speech
            audioQuality: {
              stereo: false,
              opusMaxAverageBitrate: 64000,
              enableOpusDtx: true,
            },
            enableOpusRed: true,
            // Disable P2P so audioQuality settings apply in 1-on-1 calls
            p2p: {
              enabled: false,
            },
            // Lobby configuration
            lobby: {
              enabled: true,
              autoKnock: false, // Don't auto-knock, wait for patient to request
              enableChat: false,
              showHangUp: true
            },
            securityUi: {
              hideLobbyButton: false,
              disableLobbyPassword: true
            },
            constraints: {
              video: {
                height: {
                  ideal: 720,
                  max: 720,
                  min: 240
                }
              }
            },
            recordingService: {
              enabled: false,
              sharingEnabled: false
            },
            liveStreamingEnabled: false,
            fileRecordingsEnabled: false,
            transcribingEnabled: !isInternal,
            toolbarButtons: [
              'microphone',
              'camera',
              // CAPTIONS: the clinician's, and only the clinician's.
              //
              // Not a cosmetic split — it follows from what the button actually
              // is. Jitsi has no "show captions on my own screen" switch. The CC
              // button dispatches `setRequestingSubtitles`, which marks you as
              // requesting transcription, and the transcriber stays in the room
              // while *anybody* is requesting. Rendering needs both flags
              // (`AbstractCaptions.tsx:45` returns null unless
              // `_requestingSubtitles && _displaySubtitles`), so there is no
              // display-only mode to hand out. Seeing captions IS asking for the
              // transcriber.
              //
              // For the patient that made it a way to hold transcription open
              // against the clinician's pause: captions on, pill reading
              // "Paused", consultation still going into the chart. What gets
              // captured is a clinical decision, so it is not theirs to make —
              // they have no capture control at all, and the JWT withholds the
              // `transcription` feature so the platform enforces it too
              // (`_requestingSubtitlesChange` will not dial the transcriber
              // without it).
              //
              // The clinician keeps it, in the place they already reach for. It
              // cannot drift from the Live/Paused pill any more: both surfaces
              // now read the same room state — the pill from
              // `transcribingStatusChanged`, the button from redux — and both
              // write through the same `setRequestingSubtitles`. Two ways to
              // reach one switch is fine. Two switches was the defect.
              ...(config.userRole === 'moderator' && !isInternal ? ['closedcaptions'] : []),
              'desktop',
              'fullscreen',
              'chat',
              'raisehand',
              'participants-pane',
              'tileview',
              'select-background',
              'settings',
              ...(config.userRole === 'moderator' && !isInternal ? ['invite'] : []),
              'hangup'
            ]
          }}

          interfaceConfigOverwrite={{
            APP_NAME: 'BrightLight Health',
            BRAND_WATERMARK_LINK: '',
            SHOW_JITSI_WATERMARK: false,
            SHOW_BRAND_WATERMARK: false,
            SHOW_POWERED_BY: false,
            DEFAULT_REMOTE_DISPLAY_NAME: isInternal ? 'Team member' : 'Patient',
            SETTINGS_SECTIONS: ['devices', 'language', 'profile', 'more'],
            VIDEO_LAYOUT_FIT: 'both',
            MOBILE_APP_PROMO: false,
            SHOW_PROMOTIONAL_CLOSE_PAGE: false,
            HIDE_INVITE_MORE_HEADER: true,
            DISABLE_PRESENCE_STATUS: false,
            DISABLE_JOIN_LEAVE_NOTIFICATIONS: true
          }}

          userInfo={{
            displayName: config.userName || 'User',
            email: config.userEmail || config.email || ''
          }}

          onApiReady={(externalApi) => {
            setInMeeting(true);
            window.jitsiAPI = externalApi;
            apiRef.current = externalApi;
            setJitsiLoadFailed(false);

            if (config.userRole === 'moderator' && !isInternal) {
              externalApi.on('participantJoined', async (participant) => {
                let userId = null;
                try {
                  const roomsInfo = await externalApi.getRoomsInfo();
                  const mainRoom = roomsInfo?.rooms?.find(r => r.isMainRoom);
                  const fullInfo = mainRoom?.participants?.find(p => p.id === participant.id);
                  userId = fullInfo?.userContext?.id || null;
                } catch (e) {}

                const existing = attendeesRef.current.get(participant.id);
                if (existing) {
                  existing.leftAt = null;
                  existing.rejoins = (existing.rejoins || 0) + 1;
                  if (userId) existing.userId = userId;
                } else {
                  attendeesRef.current.set(participant.id, {
                    participantId: participant.id,
                    displayName: participant.displayName || 'Unknown',
                    userId,
                    joinedAt: new Date().toISOString(),
                    leftAt: null,
                    rejoins: 0
                  });
                }

                // Auto-rename phone callers to patient name for moderator view
                const displayName = participant.displayName || '';
                const phoneMatch = displayName.match(/\(\+?(\d+)\)/);
                if (phoneMatch && config?.appointmentInfo?.patientPhone) {
                  const callerDigits = phoneMatch[1].replace(/^1/, ''); // strip leading 1 for NA
                  const patientDigits = config.appointmentInfo.patientPhone.replace(/\D/g, '').replace(/^1/, '');
                  if (callerDigits.endsWith(patientDigits) || patientDigits.endsWith(callerDigits)) {
                    if (config.appointmentInfo.patientName) {
                      externalApi.executeCommand('overwriteNames', [
                        { id: participant.id, name: `${config.appointmentInfo.patientName} (Phone)` }
                      ]);
                    }
                  }
                }
              });

              externalApi.on('participantLeft', (participant) => {
                const attendee = attendeesRef.current.get(participant.id);
                if (attendee) {
                  attendee.leftAt = new Date().toISOString();
                }
              });

              // `transcribingStatusChanged` — transcrib-ING, not transcript-ION.
              //
              // The old name does not exist in the API (grep external_api.js:
              // zero hits), so this listener had never fired. Transcription
              // state was inferred from chunks arriving, which can only ever
              // say "on" — nothing was left to say "off", and the pill sat on
              // whatever it last assumed.
              //
              // This is the room's truth, not ours: it fires when the
              // transcriber joins or leaves, whoever caused it. Driving the
              // pill from it means the label cannot disagree with the room —
              // including when the change came from somewhere that is not the
              // pill at all.
              externalApi.addListener('transcribingStatusChanged', (event) => {
                if (typeof event?.on !== 'boolean') return;
                if (event.on) transcriberEverOnRef.current = true;
                setTranscriptionPaused(!event.on);
                transcriptionPausedRef.current = !event.on;
              });

              externalApi.addListener('transcriptionChunkReceived', async (event) => {
                // A chunk arriving is proof capture is running, whatever the
                // status event has or has not told us yet. Correct the button
                // rather than keep a second flag that can disagree with it.
                if (transcriptionPausedRef.current) setTranscriptionPaused(false);

                // PAUSED: discard chunk, don't send to backend
                if (transcriptionPausedRef.current) return;

                // IMPORTANT: Only process FINAL chunks to avoid accumulation
                // Stable chunks are intermediate and cause repetition
                if (!event.data?.final) {
                  return;
                }

                const transcriptText = event.data.final;
                if (transcriptText && meetingStartTimeRef.current && config?.appointmentId) {
                  const chunk = {
                    time: formatDuration(Date.now() - meetingStartTimeRef.current),
                    speaker: event.data.participant?.name || "Unknown",
                    text: transcriptText,
                    // The same phrase arrives twice — once here and once on the
                    // JaaS webhook — and JaaS stamps both copies with the same
                    // messageID. Forwarding it is what lets the server collapse
                    // them into one instead of storing the visit twice.
                    messageId: event.data.messageID || null
                  };

                  setTranscriptionChunks(prev => {
                    const updated = [...prev, chunk];
                    return updated;
                  });

                  // Send chunk immediately to backend.
                  //
                  // `keepalive` matters: without it the browser aborts any
                  // in-flight request the moment the page navigates away, so
                  // the last phrases of EVERY call — the ones sent while the
                  // clinician is hanging up — were dropped. This is the
                  // redundant path (the JaaS webhook delivers the same phrases
                  // server-side, deduped on messageID), but redundancy that
                  // silently truncates is worse than none.
                  try {
                    await fetch(`${API_URL}/api/public/meeting/save-chunk`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      keepalive: true,
                      body: JSON.stringify({
                        // The server reads the appointment and the tenant from
                        // this token, not from the body — a transcript write has
                        // to be provably about the visit the caller was invited
                        // to. Sending appointmentId/db here would be ignored.
                        t: meetingToken,
                        chunk,
                        roomId,
                        jaasSessionId: jaasSessionIdRef.current
                      })
                    });
                  } catch (error) {
                    console.error('Failed to send chunk:', error);
                  }
                }
              });

              fetchWaitingRoom();
            }

            // Shared finalize + redirect logic
            const finalizeAndLeave = () => {
              if (finalizeTimeoutRef.current) {
                clearTimeout(finalizeTimeoutRef.current);
                finalizeTimeoutRef.current = null;
              }

              transcriptionPausedRef.current = false;
              setTranscriptionPaused(false);
              setInMeeting(false);

              // Internal: nothing to finalize, no feedback survey. The clinic opened
              // this tab, so it may close it; if the browser refuses, say so.
              if (config.meetingKind === 'internal') {
                setLeftInternal(true);
                window.close();
                return;
              }

              if (config.userRole === 'moderator' && config?.appointmentId && jaasSessionIdRef.current) {
                try {
                  const blob = new Blob([JSON.stringify({
                    t: meetingToken,
                    roomId,
                    jaasSessionId: jaasSessionIdRef.current
                  // See the beforeunload handler: text/plain avoids a CORS
                  // preflight on a request sent during page teardown.
                  })], { type: 'text/plain' });
                  navigator.sendBeacon(`${API_URL}/api/public/meeting/finalize-transcription`, blob);
                } catch (error) {
                  console.error('Failed to finalize transcription:', error);
                }
              }

              // Send attendance data
              if (config.userRole === 'moderator' && config?.appointmentId) {
                try {
                  const attendees = Array.from(attendeesRef.current.values());
                  attendees.push({
                    participantId: 'moderator',
                    displayName: config.userName,
                    userId: config.userId,
                    joinedAt: meetingStartTimeRef.current ? new Date(meetingStartTimeRef.current).toISOString() : new Date().toISOString(),
                    leftAt: new Date().toISOString(),
                    role: 'moderator'
                  });
                  // The server takes the appointment and the clinic from the
                  // link's token; the beacon carries only the room and who came.
                  const attBlob = new Blob([JSON.stringify({
                    t: meetingToken,
                    roomId,
                    attendees,
                  })], { type: 'application/json' });
                  navigator.sendBeacon(`${API_URL}/api/public/meeting/save-attendance`, attBlob);
                } catch (e) {}
              }

              if (config.userRole !== 'moderator') {
                // The feedback is recorded against this visit: the link's token
                // says which one, and in which clinic.
                window.location.href = `/feedback/${roomId}?t=${encodeURIComponent(meetingToken || '')}`;
              } else {
                window.location.href = '/';
              }
            };

            // readyToClose: authoritative hangup signal (never fires on breakout transitions)
            externalApi.on('readyToClose', () => {

              finalizeAndLeave();
            });

            // videoConferenceLeft: debounce to handle breakout room transitions
            // If doctor rejoins within 5s (breakout transition), timer is cancelled
            externalApi.on('videoConferenceLeft', () => {
              setInMeeting(false);
              finalizeTimeoutRef.current = setTimeout(() => {

                finalizeAndLeave();
              }, 5000);
            });

            // videoConferenceJoined: detect breakout vs main room via breakoutRoom property
            externalApi.on('videoConferenceJoined', (event) => {

              setInMeeting(true);
              const isBreakout = event?.breakoutRoom === true;

              isInBreakoutRef.current = isBreakout;

              // Cancel any pending finalize timer (doctor rejoined a room)
              if (finalizeTimeoutRef.current) {
                clearTimeout(finalizeTimeoutRef.current);
                finalizeTimeoutRef.current = null;
              }

              if (!initialSessionCreatedRef.current) {
                // FIRST JOIN: create session, set start time
                const startTime = Date.now();
                setMeetingStartTime(startTime);
                meetingStartTimeRef.current = startTime;
                initialSessionCreatedRef.current = true;
                // Ask 8x8 who this session is, rather than minting our own id.
                resolveJaasSessionId(externalApi);

                // Internal only (D4): record that someone actually entered the
                // room, feeding the server's no-show window. Best-effort and
                // fire-and-forget — a failure here must never block or alter
                // the call itself.
                if (shouldSendMeetingEntered({ isInternal, alreadySent: meetingEnteredSentRef.current })) {
                  meetingEnteredSentRef.current = true;
                  sendMeetingEntered({
                    fetchFn: fetch,
                    apiUrl: API_URL,
                    token: meetingToken
                  }).catch(() => {
                    // The token is a bearer credential for this room, so it is
                    // deliberately left out of the log.
                    console.warn('Failed to record meeting entry after retries');
                  });
                }

                // AUTO-START transcription when the appointment asks for it.
                //
                // JaaS does NOT transcribe retroactively: it captures from the
                // exact moment the feature is switched on. A clinician who
                // remembered the CC button ten minutes in produced a note that
                // began mid-consultation, and one who never remembered produced
                // nothing at all — neither of which surfaced as an error.
                if (config?.enableAITranscription && config?.userRole === 'moderator') {
                  setTranscription(externalApi, true);
                  setTimeout(() => {
                    showToast(
                      'AI Transcription On',
                      'This consultation is being transcribed. Use the button at the bottom right to pause.',
                      'info'
                    );
                  }, 3000);

                  // Asking is not the same as it happening. Dialling the
                  // transcriber can fail — a JaaS hiccup, a slow room — and
                  // until now that failure was completely silent: the toast
                  // above still claimed the consultation was being
                  // transcribed, and the clinician found out at the end, when
                  // there was no note and no audio to go back to.
                  //
                  // `transcribingStatusChanged` is the room saying the
                  // transcriber actually joined. If it has not said so by now,
                  // say so out loud while the visit can still be saved.
                  setTimeout(() => {
                    if (transcriberEverOnRef.current) return;
                    setTranscriptionPaused(true);
                    transcriptionPausedRef.current = true;
                    showToast(
                      'Transcription did not start',
                      'Nothing is being recorded for the note. Press the button at the bottom right to start it.',
                      'error'
                    );
                  }, 15000);
                }
              } else if (isBreakout && config.meetingKind !== 'internal') {

                // ENTERING BREAKOUT: auto-pause transcription, keep same session.
                //
                // The transcriber is stopped, not merely ignored. A breakout is
                // where a clinician takes someone aside; suppressing our own
                // relay while 8x8 kept transcribing meant the aside was written
                // into the chart anyway, by the webhook.
                transcriptionPausedRef.current = true;
                setTranscriptionPaused(true);
                setTranscription(externalApi, false);
              } else if (config.meetingKind !== 'internal') {

                // RETURNING TO MAIN ROOM: resume transcription, keep same session.
                //
                // This used to toggle OFF, wait for the status event, toggle ON
                // at +500ms, and then run an unconditional "fallback" toggle at
                // +3000ms. When the normal path worked, that fallback fired on
                // top of it and turned transcription back OFF — so every visit
                // that used a breakout room lost its transcript from that point
                // on, silently. With an explicit setter there is no state to
                // race against: assert OFF, then assert ON to reset Jigasi.
                transcriptionPausedRef.current = false;
                setTranscriptionPaused(false);
                setTranscription(externalApi, false);
                setTimeout(() => setTranscription(externalApi, true), 800);
              }

              if (config.userRole === 'moderator' && config.meetingKind !== 'internal') {
                fetchWaitingRoom();
              }
            });
          }}

          getIFrameRef={(iframeRef) => {
            if (iframeRef) {
              iframeRef.style.height = '100%';
              iframeRef.style.width = '100%';
            }
          }}
        />
      </React.Suspense>
    </div>
  );
};

export default Meeting;